import { AxiosResponse } from 'axios';
import $api from './instance';
import {
  CreatePaymentRequest,
  PaymentResponse,
  PaymentStatusResponse as PaymentStatus,
  PaymentListResponse,
  PaymentStatisticsData,
  PaymentStatisticsResponse,
  PeriodStatisticsData,
  PeriodStatisticsResponse,
  PaymentTransaction,
  PaymentsTransactionsResponse,
  Debtor,
  DebtorsResponse,
} from '@/types/payments';

// === ІСНУЮЧІ ФУНКЦІЇ ===

export async function createPaymentRequest(
  data: CreatePaymentRequest,
): Promise<
  AxiosResponse<{ success: boolean; data: PaymentResponse; message: string }>
> {
  return $api.post('/payments/create', data);
}

export async function getPaymentStatusRequest(
  paymentId: string,
): Promise<AxiosResponse<{ success: boolean; data: PaymentStatus }>> {
  return $api.get(`/payments/${paymentId}/status`);
}

export async function getPaymentRequest(
  paymentId: string,
): Promise<AxiosResponse<{ success: boolean; data: PaymentResponse }>> {
  return $api.get(`/payments/${paymentId}`);
}

export async function getUserPaymentsRequest(
  limit: number = 20,
  offset: number = 0,
): Promise<AxiosResponse<PaymentListResponse>> {
  return $api.get(`/payments?limit=${limit}&offset=${offset}`);
}

export async function testPaymentConnectionRequest(): Promise<
  AxiosResponse<{
    success: boolean;
    message: string;
    environment: string;
  }>
> {
  return $api.get('/payments/test-connection');
}

export async function createTestPaymentRequest(): Promise<
  AxiosResponse<{
    success: boolean;
    data: PaymentResponse;
    message: string;
  }>
> {
  return $api.post('/payments/sandbox/test-payment');
}

// === ФУНКЦІЇ СТАТИСТИКИ ТА ЗВІТІВ ===

/**
 * Отримати загальну статистику по платежам
 * GET /payments/statistics
 */
export async function getPaymentStatisticsRequest(): Promise<AxiosResponse<PaymentStatisticsResponse>> {
  return $api.get<PaymentStatisticsResponse>('/payments/statistics');
}

/**
 * Отримати статистику по платежам за періодом
 * GET /payments/statistics/:period
 */
export async function getPaymentStatisticsByPeriodRequest(
  period: 'this-month' | 'last-month' | 'this-quarter' | 'this-year'
): Promise<AxiosResponse<PeriodStatisticsResponse>> {
  return $api.get<PeriodStatisticsResponse>(`/payments/statistics/${period}`);
}

/**
 * Отримати транзакції з фільтрами
 * GET /payments/transactions
 */
export async function getPaymentsTransactionsRequest(
  filters: {
    period?: string;
    propertyId?: string;
    tenantId?: string;
    limit?: number;
    offset?: number;
  } = {}
): Promise<AxiosResponse<PaymentsTransactionsResponse>> {
  const params = new URLSearchParams();

  if (filters.period && filters.period !== 'all-time') {
    params.append('period', filters.period);
  }
  if (filters.propertyId && filters.propertyId !== 'all-properties') {
    params.append('propertyId', filters.propertyId);
  }
  if (filters.tenantId && filters.tenantId !== 'all-tenants') {
    params.append('tenantId', filters.tenantId);
  }
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.offset) params.append('offset', filters.offset.toString());

  const queryString = params.toString();
  const url = queryString ? `/payments/transactions?${queryString}` : '/payments/transactions';

  return $api.get<PaymentsTransactionsResponse>(url);
}

/**
 * Отримати список орендарів-боржників
 * GET /payments/debtors
 */
export async function getDebtorsRequest(): Promise<AxiosResponse<DebtorsResponse>> {
  return $api.get<DebtorsResponse>('/payments/debtors');
}

/**
 * Завантажити повний звіт по платежам
 * GET /payments/getFullReport
 */
export async function getFullReportRequest(filters: {
  period?: string;
  propertyId?: string;
  tenantId?: string;
} = {}): Promise<AxiosResponse<Blob>> {
  const params = new URLSearchParams();

  if (filters.period && filters.period !== 'all-time') {
    params.append('period', filters.period);
  }
  if (filters.propertyId && filters.propertyId !== 'all-properties') {
    params.append('propertyId', filters.propertyId);
  }
  if (filters.tenantId && filters.tenantId !== 'all-tenants') {
    params.append('tenantId', filters.tenantId);
  }

  const queryString = params.toString();
  const url = queryString ? `/payments/getFullReport?${queryString}` : '/payments/getFullReport';

  return $api.get(url, {
    responseType: 'blob', // Важливо для завантаження файлів
  });
}

// Експортуємо типи для зворотної сумісності
export type {
  CreatePaymentRequest,
  PaymentResponse,
  PaymentStatus,
  PaymentListResponse,
  PaymentStatisticsData,
  PeriodStatisticsData,
  PaymentTransaction,
  Debtor,
} from '@/types/payments';